import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { caseAPI } from '../api';
import Logo from '../components/Logo';

export default function CaseDetails() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading, logout } = useAuth();
  
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadCase = async () => {
    try {
      const response = await caseAPI.getCaseDetails(caseId);
      setCaseData(response.data);
      setError(null);
    } catch (err) {
      const status = err.response?.status;
      
      if (status === 404) {
        setError('Case not found');
        setTimeout(() => navigate('/dashboard'), 2000);
      } else if (status === 401 || status === 403) {
        setError('Authentication required');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setError('Failed to load case details');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      loadCase();
    }
  }, [caseId, authLoading, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this case?')) {
      return;
    }

    setDeleting(true);
    try {
      await caseAPI.deleteCase(caseId);
      navigate('/dashboard');
    } catch (err) {
      alert('Failed to delete case');
      setDeleting(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading case details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-gray-900 font-medium">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 text-indigo-600 hover:text-indigo-700"
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Helper to format dates safely
  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Not available';
      return date.toLocaleString();
    } catch {
      return 'Not available';
    }
  };

  if (!caseData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-gray-600 hover:text-gray-900 text-sm"
              >
                ← Back
              </button>
              <Logo className="hidden sm:flex" />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-3 py-2 text-sm font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
              <button
                onClick={() => logout()}
                className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-700"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-900">
            {caseData.nickname || caseData.receipt_number}
          </h1>
          {caseData.nickname && (
            <p className="text-sm text-gray-500">{caseData.receipt_number}</p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Current Status</h2>
            <p className="text-2xl font-bold text-indigo-600">{caseData.official_status_title}</p>
          </div>

          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-2">AI Summary</h3>
            <p className="text-gray-600 leading-relaxed">{caseData.ai_summary}</p>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Last Checked</p>
              <p className="font-medium text-gray-900">{formatDate(caseData.checked_at)}</p>
              <p className="text-xs text-indigo-600 mt-1">Auto-updating every {caseData.tracking_frequency || '6'} hours</p>
            </div>
            <div>
              <p className="text-gray-500">Last Updated</p>
              <p className="font-medium text-gray-900">{formatDate(caseData.last_updated)}</p>
            </div>
          </div>
        </div>

        {caseData.history && caseData.history.length > 0 ? (
          <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Status History ({caseData.history.length})</h2>
            <div className="space-y-4">
              {caseData.history.map((item, index) => (
                <div key={item.id || index} className="border-l-2 border-indigo-200 pl-4">
                  <p className="font-medium text-gray-900">{item.official_status_title}</p>
                  <p className="text-sm text-gray-500">{formatDate(item.checked_at)}</p>
                  {item.ai_summary && (
                    <p className="text-sm text-gray-600 mt-1">{item.ai_summary}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Status History</h2>
            <p className="text-gray-500 text-sm">No history available yet. Check back after the next status update.</p>
          </div>
        )}
      </main>
    </div>
  );
}
